import { Link } from 'react-router-dom';
import { Building2, Plus } from 'lucide-react';
import { useOrganizations } from '@repo/queries';
import { useActiveOrganizationId, useSetActiveOrganization } from '@repo/api-client/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { displayWireMoney } from '@/features/money';
import { BusinessAvatar } from '@/features/avatar';
import { brandingColor } from '@/features/avatar/branding';

export function BusinessesListScreen() {
  const { organizations, loading } = useOrganizations();
  const activeId = useActiveOrganizationId();
  const setActive = useSetActiveOrganization();

  return (
    <Page>
      <PageHeader
        title="Businesses"
        description="Each workspace has its own clients, invoices, and tax settings."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/settings/businesses/archived">Archived</Link>
            </Button>
            <Button asChild>
              <Link to="/settings/businesses/new">
                <Plus className="size-4" /> New business
              </Link>
            </Button>
          </>
        }
      />

      {loading ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 4 }).map((_, idx) => (
            <Skeleton key={idx} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : !organizations || organizations.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<Building2 className="size-7" />}
              title="No businesses yet"
              description="Create your first workspace to start sending invoices."
              actions={
                <Button asChild>
                  <Link to="/settings/businesses/new">
                    <Plus className="size-4" /> New business
                  </Link>
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {organizations.map((org) => (
            <Card
              key={org.id}
              className="flex h-full flex-col justify-between transition-colors hover:border-line-strong"
            >
              <CardContent className="flex flex-col gap-3 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <BusinessAvatar
                      id={org.id}
                      name={org.name}
                      initials={org.nickname ? org.nickname.slice(0, 3).toUpperCase() : undefined}
                      color={brandingColor({ id: org.id, branding: org.branding })}
                      logoUrl={org.logoUrl ?? null}
                      size={44}
                    />
                    <div className="min-w-0">
                      <h3 className="truncate text-base font-semibold text-foreground">
                        {org.nickname || org.name}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        {org.currency} · {org.role}
                      </p>
                    </div>
                  </div>
                  {activeId === org.id ? <Badge variant="accent">Active</Badge> : null}
                </div>
                {org.stats ? (
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <Stat label="Outstanding" value={displayWireMoney(org.stats.outstandingMinor, org.stats.currency)} />
                    <Stat label="Invoices" value={String(org.stats.invoiceCount)} />
                    <Stat label="Clients" value={String(org.stats.clientCount)} />
                  </div>
                ) : null}
                <div className="mt-auto flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" asChild>
                    <Link to={`/settings/businesses/${org.id}`}>Manage</Link>
                  </Button>
                  {activeId !== org.id ? (
                    <Button size="sm" className="flex-1" onClick={() => setActive(org.id)}>
                      Switch to
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-line bg-secondary/30 p-2">
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div className="font-mono text-sm text-foreground">{value}</div>
    </div>
  );
}
